/**
 * AiChatArea
 * Main chat area for AI conversations. Uses Vercel AI SDK v5 useChat hook for streaming.
 */

import { useChat } from "@ai-sdk/react"
import { useQueryClient } from "@tanstack/react-query"
import { DefaultChatTransport } from "ai"
import { Archive, ArrowLeft, MoreVertical, Trash2 } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAccessToken } from "@/lib/token-manager"
import {
  useAiConversation,
  useDeleteAiConversation,
  useUpdateAiConversation,
} from "../../hooks/useAiChat"
import { getMessageStreamUrl } from "../../services/ai-chat.service"
import { MessageInput } from "../shared/MessageInput"
import { ChatMessageBubble } from "./ChatMessageBubble"

function AiChatAreaInner({ id }: { id: string }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const archiveConversation = useUpdateAiConversation()
  const deleteConversation = useDeleteAiConversation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const firstMessageSent = useRef(false)
  const initialLoadDone = useRef(false)
  const wasStreamingRef = useRef(false)
  const [inputValue, setInputValue] = useState("")

  // Fetch existing conversation data
  const { data: conversationData, isLoading: isConversationLoading } = useAiConversation(id)

  // Create transport for the AI SDK
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getMessageStreamUrl(id),
        headers: () => ({
          Authorization: `Bearer ${getAccessToken()}`,
        }),
      }),
    [id],
  )

  const { messages, sendMessage, status, stop, setMessages, error } = useChat({
    id,
    transport,
  })

  const isLoading = status === "streaming" || status === "submitted"

  // Load existing messages from API into chat state — exactly once per
  // conversation (id changes force a full remount via key={id}). Re-running on
  // status change would overwrite freshly-streamed messages with stale cache
  // and make new turns disappear after the stream completes.
  //
  // Extra guards: the initial fetch can resolve mid-stream when a slow API
  // race against an eager `firstMessage` send, so we also bail if local
  // chat state already has messages or a stream is in flight.
  useEffect(() => {
    if (initialLoadDone.current) return
    if (!conversationData?.messages) return
    if (messages.length > 0) return
    if (status === "streaming" || status === "submitted") return

    if (conversationData.messages.length > 0) {
      const existingMessages = conversationData.messages.map(msg => ({
        id: msg.id,
        role: msg.role as "user" | "assistant",
        parts:
          msg.parts && msg.parts.length > 0
            ? (msg.parts as Array<{ type: "text"; text: string }>)
            : [{ type: "text" as const, text: msg.content }],
      }))
      setMessages(existingMessages)
    }
    initialLoadDone.current = true
  }, [conversationData?.messages, messages.length, setMessages, status])

  // Refresh the conversations cache when a stream finishes so the sidebar
  // reflects new lastMessageAt + previews. Title generation happens in a
  // separate fire-and-forget LLM call after the SSE response closes, so a
  // second invalidation after a short delay picks up the new title without
  // forcing the server to block the response on title generation.
  useEffect(() => {
    const isStreamingNow = status === "streaming" || status === "submitted"
    if (wasStreamingRef.current && !isStreamingNow) {
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["ai-conversations"] })
        queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
      }
      invalidate()
      const timer = setTimeout(invalidate, 6000)
      wasStreamingRef.current = isStreamingNow
      return () => clearTimeout(timer)
    }
    wasStreamingRef.current = isStreamingNow
  }, [status, queryClient])

  // Handle the optional `firstMessage` query param — sent as a real user
  // message so it gets persisted normally. Coach intro greeting + prep are
  // now persisted server-side at conversation-create time, so they no longer
  // need URL-param injection.
  useEffect(() => {
    const firstMessage = searchParams.get("firstMessage")

    if (!id || firstMessageSent.current) return
    if (!firstMessage) return

    firstMessageSent.current = true
    setSearchParams({}, { replace: true })
    sendMessage({ text: firstMessage })
  }, [id, searchParams, setSearchParams, sendMessage])

  // Auto-scroll to bottom on new messages
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll should trigger on every message change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Log stream errors for debugging
  useEffect(() => {
    if (error) {
      console.error("[AiChat] Stream error:", error)
    }
  }, [error])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return
    sendMessage({ text: inputValue })
    setInputValue("")
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="h-14 px-[30px] flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2.5 min-w-0">
          <Button variant="ghost" size="icon-xs" asChild className="md:hidden">
            <Link to="/chats">
              <ArrowLeft className="size-5" />
            </Link>
          </Button>
          <img src={aiAvatarImg} alt="" className="size-[30px] rounded-full shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-foreground truncate">
              {(() => {
                const meta = conversationData?.conversation?.metadata as
                  | { ritualTitle?: string }
                  | null
                  | undefined
                return meta?.ritualTitle || "My Rituality AI"
              })()}
            </span>
            {(() => {
              const meta = conversationData?.conversation?.metadata as
                | { type?: string }
                | null
                | undefined
              if (meta?.type === "coach") {
                return (
                  <span className="text-[11px] text-muted-foreground">
                    Personal Rituality Coach
                  </span>
                )
              }
              if (meta?.type === "reflect") {
                return <span className="text-[11px] text-muted-foreground">Ritual reflection</span>
              }
              return null
            })()}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="More options"
              className="text-foreground hover:opacity-70 transition-opacity"
              title="More options"
            >
              <MoreVertical className="size-6" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem
              onClick={() =>
                archiveConversation.mutate(
                  { id, data: { status: "archived" } },
                  {
                    onSuccess: () => {
                      toast.success("Conversation archived")
                      navigate("/chats")
                    },
                    onError: () => toast.error("Failed to archive conversation"),
                  },
                )
              }
            >
              <Archive />
              Archive
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                deleteConversation.mutate(id, {
                  onSuccess: () => {
                    toast.success("Conversation deleted")
                    navigate("/chats")
                  },
                  onError: () => toast.error("Failed to delete conversation"),
                })
              }
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-[30px] py-4 space-y-4">
        {isConversationLoading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="size-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!isConversationLoading && messages.length === 0 && !isLoading && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-text-body opacity-50">
              Send a message to start the conversation
            </p>
          </div>
        )}
        <div className="max-w-[925px] mx-auto flex flex-col gap-5">
          {messages.map(message => (
            <ChatMessageBubble key={message.id} message={message} />
          ))}
        </div>
        {error && (
          <div className="flex justify-start">
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400">
              {(() => {
                const msg = String(error.message || "")
                if (msg.includes("401") || msg.includes("403")) {
                  return "Your session may have expired. Please refresh and try again."
                }
                if (msg.includes("429") || /too many requests/i.test(msg)) {
                  return "You're sending messages a bit too fast. Please wait a moment and try again."
                }
                return "Something went wrong. Please try sending your message again."
              })()}
            </div>
          </div>
        )}
        {isLoading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="flex gap-1.5 px-2 py-2">
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:150ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:300ms]" />
              <div className="size-2.5 rounded-full bg-[var(--color-brand-olive)] animate-bounce [animation-delay:450ms]" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        input={inputValue}
        onInputChange={setInputValue}
        onSubmit={handleSubmit}
        onStop={stop}
        isLoading={isLoading}
        autoFocus
      />
    </div>
  )
}

export function AiChatArea() {
  const { id } = useParams<{ id: string }>()
  if (!id) return null
  // key={id} forces full remount when conversation changes, resetting all chat state
  return <AiChatAreaInner key={id} id={id} />
}
